import { gql, useQuery } from '@apollo/client'
import { Playlist } from '@frontend-mono/shared/data-access-graphql'
import { useMemo } from 'react'

type Response = {
  currentUser: {
    id: string
    playlists: Playlist[]
  }
}

export type PlaylistItem = {
  id: string
  title: string
  count: number
  pinned: boolean
  topicIds: string[]
}

export const GET_PLAYLISTS = gql`
  query GetPlaylist {
    currentUser {
      id
      playlists {
        id
        title
        pinned
        topics {
          id
          title
          isOriginalContent
          tags {
            tag
          }
          slug
          lessonCount {
            total
          }
          currentUserEnrollment {
            id
            status
            graduatedAt
            review {
              id
              content
              score
            }
            progress
            passedLessonCount
            topic {
              id
            }
            nextLesson {
              id
              seqId
            }
          }
          chapters(first: 1) {
            id
            typedId
            lessons(first: 1) {
              id
              seqId
            }
          }
        }
      }
    }
  }
`

export const useGetPlaylists = () => {
  const { data, loading, refetch } = useQuery<Response>(GET_PLAYLISTS, {
    fetchPolicy: 'cache-and-network',
  })

  const playlists = useMemo(() => {
    const targetPlaylists = data?.currentUser?.playlists?.map((item) => ({
      id: item.id,
      title: item.title,
      count: item.topics.length,
      pinned: item.pinned,
      topicIds: item.topics.map((topic) => topic?.id),
      topics: item.topics,
    })) || []
    return targetPlaylists
  }, [data])

  const hasAlready = (playlistId: string, topicIds: string[]): boolean => {
    const selectedPlaylist = playlists.find((item) => item.id === playlistId)
    return (
      selectedPlaylist?.topicIds.some((topicId) => topicIds.includes(topicId)) || false
    )
  }

  return {
    playlists,
    loading,
    hasPinnedPlaylist: playlists.some((item) => item.pinned),
    hasAlready,
    refetch,
  }
}
